import { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, Download } from "lucide-react";
import Questions from "./Questions";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import MCQImportTemplate from "../../../../assets/MCQ Import Template.xlsx";
import { Trash2 } from "lucide-react";
import ConfirmDialogBox from "../../../confirmdiolog/ConfirmDiologBox";
import AssessmentService from "../../../../services/AssessmentService";


const generateId = () =>
  "ui_" + Math.random().toString(36).substring(2, 9);

const createEmptyQuestion = (order) => ({
  id: generateId(),   
  serverId: null,      
  question: "",
  orderBy: order,
  hasGraceMark: false,
  difficulty: 1,
  options: [
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 1,
    },
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 2,
    },
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 3,
    },
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 4,
    },
  ],
});

const AssessmentQuestions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const authContext = useContext(AuthContext);
  const [showDeleteAssessmentDialog, setShowDeleteAssessmentDialog] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [toBeDeleted, setToBeDeleted] = useState([]);



 const fetchExistingQuestions = useCallback(async () => {
    try {
      const res = await AssessmentService.GetAllQuestions(id, authContext);

      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

      if (list.length > 0) {
        const mapped = list.map((q, qi) => ({
          id: generateId(),      
          serverId: q.id,     
          question: q.question,
          orderBy: q.orderBy ?? qi + 1,
          hasGraceMark: q.hasGraceMark ?? false,
          difficulty: q.difficulty ?? 1,
          options: q.options
            .sort((a, b) => a.orderBy - b.orderBy)
            .map((o, oi) => ({
              id: generateId(),  
              serverId: o.id,  
              option: o.option,
              isAnswer: o.isAnswer,
              orderBy: o.orderBy ?? oi + 1,
            })),
        }));

        setQuestions(mapped);
      } else {
        setQuestions([createEmptyQuestion(1)]);
      }
    } catch (err) {
      console.error("Failed to fetch assessment questions", err);
      setQuestions([createEmptyQuestion(1)]);
    }
}, [authContext, id]);

  
  useEffect(() => {
    fetchExistingQuestions();
  }, [fetchExistingQuestions]);


  const addQuestion = () => {
    setQuestions((prev) => [...prev, createEmptyQuestion(prev.length + 1)]);
  };

  const removeQuestion = (qid) => {
    setQuestions((prev) => {
      const removed = prev.find((q) => q.id === qid);

      if (removed?.serverId) {
        setToBeDeleted((d) => [...d, removed.serverId]);
      }

      return prev
        .filter((q) => q.id !== qid)
        .map((q, i) => ({ ...q, orderBy: i + 1 }));
    });
  };

  const updateQuestion = (qid, patch) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === qid ? { ...q, ...patch } : q))
    );
  };

  const submitHandler = async () => {
    try {
      const payload = {
        toBeDeleted: toBeDeleted,
        models: questions.map((q) => ({
          ...(q.serverId && { id: q.serverId }), 
          topicId: id,
          isConfident: false,
          question: q.question,
          hasGraceMark: q.hasGraceMark,
          difficulty: q.difficulty,
          orderBy: q.orderBy,
          options: q.options.map((o) => ({
            ...(o.serverId && { id: o.serverId }),
            ...(q.serverId && { questionId: q.serverId }),
            orderBy: o.orderBy,
            option: o.option,
            isAnswer: o.isAnswer,
          })),
        })),
      };

      const res = await AssessmentService.PostBulkQuestions(payload, authContext);
      const response = res?.data ?? res;

      if (response.isSuccess) {
        AlertService.success("Assessment questions uploaded successfully");
        navigate(-1);
      } else {
        AlertService.error(response.errorMessage || "Upload failed");
      }
    } catch (err) {
      console.error("Assessment submit failed", err);
      AlertService.error("Failed to upload assessment questions");
    }
  };

const confirmDeleteAssessment = async () => {
  try {
    const questionIds = questions
      .filter(q => q.serverId)
      .map(q => q.serverId);
    if (questionIds.length > 0) {
      await AssessmentService.PostBulkQuestions(
        {
          toBeDeleted: questionIds,
          models: [],
        },
        authContext
      );
    }
    await AssessmentService.RemoveQuestions(id, authContext);

    AlertService.success("Assessment removed successfully");
    navigate(-1);
  } catch (err) {
    console.error(err);
    AlertService.error("Failed to remove assessment");
  }
};


  return (
    <>
    <div className="w-full px-4 py-6">

    <div className="flex justify-between items-center mb-6">
 <button
  onClick={() => setShowDeleteAssessmentDialog(true)}
  className="
    flex items-center gap-2
    px-4 py-2
    rounded-md
    border border-red-500
    text-red-600

    hover:bg-red-50

    dark:border-red-400
    dark:text-red-400
    dark:hover:bg-red-500/10
  "
>
  <Trash2 size={18} />
  Delete All
</button>

  <div className="flex gap-6 text-base">
    <button
      onClick={() =>
        navigate(`/masters-assessments/${id}/questions/import`)
      }
      className="flex items-center gap-2 underline hover:text-blue-600"
    >
      <Upload size={20} />
      Upload Questions
    </button>

    <a
      href={MCQImportTemplate}
      download="MCQ Import Template.xlsx"
      className="flex items-center gap-2 underline hover:text-blue-600"
    >
      <Download size={20} />
      Download Template
    </a>
  </div>
</div>

      {/* Questions */}
      <div className="space-y-6">
        {questions.map((q, i) => (
          <Questions
            key={q.id}  
            index={i}
            input={q}
            onChange={updateQuestion}
            onRemove={removeQuestion}
          />
        ))}
      </div>

      {/* Bottom actions */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <button
          onClick={addQuestion}
          className="sm:mr-auto px-6 py-3 rounded-md bg-[#0e3a46] text-white"
        >
          Add Question
        </button>

        <div className="flex gap-4">
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-2 rounded-md border border-gray-400 hover:bg-gray-200"
          >
            Cancel
          </button>

          <button
            onClick={submitHandler}
            className="px-8 py-2 rounded-md bg-[#0e3a46] text-white"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
    <ConfirmDialogBox
  visible={showDeleteAssessmentDialog}
  onHide={() => setShowDeleteAssessmentDialog(false)}
  onConfirm={confirmDeleteAssessment}
  title={
    <>
      Are you sure you want to remove this <strong>Assessment</strong>? <br />
    </>
  }
  confirmLabel="Delete"
  cancelLabel="Cancel"
/>
</>
  );
};

export default AssessmentQuestions;
